import type * as Y from 'yjs';

import type { YJSInput } from './isYValue';

const mutationKeys: WeakMap<Y.AbstractType<any>, number> = new WeakMap();
const observedDocs: WeakSet<Y.Doc> = new WeakSet();

/**
 * Observe mutations on a Yjs input and return a number that increments on each mutation.
 */
export function getYMutationClock(input: YJSInput): number | undefined {
    if (!input.doc) {
        // The input is not linked to a doc, so it can't be observed.
        return undefined;
    }

    if (input.doc._transaction) {
        // We can't observe mutations during a transaction because observeDeep will be called at the end
        // and mutations occuring during the transaction will be missed.
        throw new Error('Cannot observe mutations during a transaction.');
    }

    observeYDocMutations(input.doc);

    return mutationKeys.get(input) || 0;
}

/**
 * Mark a Yjs value as mutated.
 */
export function markYMutation(
    input: Y.AbstractType<any>,
    recursive = true,
    changed: Set<Y.AbstractType<any>> = new Set()
) {
    if (changed.has(input)) {
        return;
    }

    const current = mutationKeys.get(input) || 0;
    mutationKeys.set(input, current + 1);
    changed.add(input);

    if (input.parent && recursive) {
        markYMutation(input.parent, recursive, changed);
    }
}

/**
 * Start observing transactions in the Yjs doc to increment the mutation clock on
 * all modified Yjs objects.
 */
function observeYDocMutations(doc: Y.Doc) {
    if (observedDocs.has(doc)) {
        return;
    }

    const callback = (tr: Y.Transaction) => {
        const changed = new Set<Y.AbstractType<any>>();

        tr.changed.forEach((_type, input) => {
            markYMutation(input, true, changed);
        });

        tr.changedParentTypes.forEach((_type, input) => {
            markYMutation(input, true, changed);
        });
    };

    doc.on('afterTransaction', callback);

    doc.on('destroy', () => {
        doc.off('afterTransaction', callback);
        observedDocs.delete(doc);
    });

    observedDocs.add(doc);
}
